import type { IdvSendTiming } from '@common/enums';
import type { CreateProspectManually } from '@common/types/prospects';
import { isValidEmail } from '@common/utils/isValidEmail';
import { formatPhoneNumber, toE164PhoneNumber } from '@common/utils/phone';
import { getPhoneValidationError } from '@/utils/phone';

// The clients in the New Prospect modal, and their checks. Kept out of the components so they
// can be tested, and reused when a client is picked from the firm's contacts

export interface ClientDetailsEntry {
    /** Identifies the row while the form is open. Not sent */
    rowId: string;
    firstName: string;
    middleName: string;
    lastName: string;
    contactEmail: string;
    /** Digits only, without the dialing code. Sent as people read it, such as (416) 555-0100 */
    phoneNumber: string;
    /** Lowercase ISO country code of the phone, such as 'ca'. Only used to read phoneNumber */
    phoneCountryCode: string;
    /**
     * When to send this client's ID verification automatically, or null not to. Undefined until
     * someone chooses, so it follows the firm's default (withDefaultIdvSendTiming)
     */
    idvSendTiming: IdvSendTiming | null | undefined;
    isPrimary: boolean;
}

export interface ClientDetailsErrors {
    firstName?: string;
    lastName?: string;
    contactEmail?: string;
    phoneNumber?: string;
}

export function createClientDetailsEntry(
    overrides: Partial<ClientDetailsEntry> = {},
): ClientDetailsEntry {
    return {
        rowId: crypto.randomUUID(),
        firstName: '',
        middleName: '',
        lastName: '',
        contactEmail: '',
        phoneNumber: '',
        phoneCountryCode: 'ca',
        idvSendTiming: undefined,
        isPrimary: false,
        ...overrides,
    };
}

/**
 * The client with the firm's default automatic ID verification, unless someone chose for them.
 * Applied when reading the clients, since the firm can load after the first client is created
 */
export function withDefaultIdvSendTiming(
    client: ClientDetailsEntry,
    defaultIdvSendTiming: IdvSendTiming | null,
): ClientDetailsEntry {
    return client.idvSendTiming === undefined
        ? { ...client, idvSendTiming: defaultIdvSendTiming }
        : client;
}

/** One client's own errors. Duplicate emails across clients are checked when the form is sent */
export function getClientDetailsErrors(client: ClientDetailsEntry): ClientDetailsErrors {
    const email = client.contactEmail.trim();
    const phoneError =
        client.phoneNumber || client.idvSendTiming
            ? getPhoneValidationError(client.phoneNumber, client.phoneCountryCode, {
                  emptyMessage: 'A cell number is needed to send ID verification',
              })
            : '';
    const idvCountryError =
        client.idvSendTiming && client.phoneCountryCode !== 'ca'
            ? 'ID verification is only sent automatically to Canadian cell numbers'
            : '';
    return {
        firstName: client.firstName.trim() ? undefined : 'First name is required.',
        lastName: client.lastName.trim() ? undefined : 'Last name is required.',
        contactEmail: !email
            ? 'Email is required.'
            : isValidEmail(email)
              ? undefined
              : 'Invalid email.',
        phoneNumber: phoneError || idvCountryError || undefined,
    };
}

export function hasClientDetailsErrors(errors: ClientDetailsErrors): boolean {
    return Object.values(errors).some(Boolean);
}

/**
 * A client as the API takes it, with the phone as people read it. getClientDetailsErrors has
 * already checked the phone, so one that isn't a valid number is only possible when it's empty
 */
export function toCreateContact(
    client: ClientDetailsEntry,
): CreateProspectManually['contacts'][number] {
    const middleName = client.middleName.trim();
    const e164PhoneNumber = toE164PhoneNumber(client.phoneNumber, client.phoneCountryCode);
    const phoneNumber = e164PhoneNumber && formatPhoneNumber(e164PhoneNumber);
    return {
        firstName: client.firstName.trim(),
        middleName: middleName || undefined,
        lastName: client.lastName.trim(),
        contactEmail: client.contactEmail.trim(),
        ...(phoneNumber && { phoneNumber }),
        idvSendTiming: client.idvSendTiming ?? null,
        isPrimary: client.isPrimary,
    };
}
